import { useMemo } from 'react';
import type { HistoryRange } from '@wave/shared';
import { EChart, isCoarsePointer, type EChartsOption } from '../EChart';
import { useIsNarrow } from '../../lib/useMediaQuery';
import { CHART_COLORS, chartGrid, tooltipFit, valueAxisLabel } from './ChartsPanel';
import { useDailyPosition } from './useBalanceHistory';
import { daysForRange, toCandles } from './dailyMath';
import { robustAxisRange } from './robustMath';

const fmt = (v: number) => v.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const fmtSigned = (v: number) => `${v > 0 ? '+' : ''}${fmt(v)}`;
const fmtDate = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${d}.${m}`;
};

/**
 * Позиция по суткам: свеча = открытие → закрытие (устойчивые точки на
 * границах суток МСК, см. chat-balances/daily.ts), усы — сырой разброс внутри
 * суток. Отвечает на вопрос «правда ли позиция N» без внутридневного шума:
 * ровная лесенка закрытий — учёт в порядке, длинный ус — днём что-то висело.
 */
export function DailyChart({ range, onPickDay }: { range: HistoryRange; onPickDay: (date: string) => void }) {
  const { data, isLoading } = useDailyPosition(daysForRange(range));
  const candles = useMemo(() => toCandles(data?.days ?? []), [data]);
  const narrow = useIsNarrow();

  const option = useMemo<EChartsOption>(() => {
    const levels = candles.flatMap((c) =>
      c.candle[0] === '-' ? [] : [c.candle[0], c.candle[1], ...(c.median === null ? [] : [c.median])],
    );
    // Шкала — по открытиям/закрытиям/медианам, не по усам: ус до ~1 млн от
    // перевода «в пути» иначе сплющит все свечи в линию. Ус за шкалой
    // обрезается — полное значение в тултипе.
    const axis = robustAxisRange([levels]);
    return {
      animation: false,
      grid: chartGrid(narrow),
      tooltip: {
        trigger: 'axis',
        ...tooltipFit(narrow),
        axisPointer: { type: 'shadow' },
        formatter: (params: unknown) => {
          const i = (params as Array<{ dataIndex: number }>)[0]?.dataIndex ?? 0;
          const c = candles[i];
          if (!c) return '';
          if (c.candle[0] === '-') return `${fmtDate(c.date)}<br/>снапшотов нет`;
          const [open, close, lo, hi] = c.candle;
          const warn = c.unstable
            ? '<br/><span style="color:#d97706">⚠ на границе суток шла операция — точка неустойчива</span>'
            : '';
          return [
            `<b>${fmtDate(c.date)}</b>`,
            `Открытие: <b>${fmt(open)}</b>`,
            `Закрытие: <b>${fmt(close)}</b> (${fmtSigned(c.delta ?? 0)})`,
            c.median === null ? '' : `Медиана дня: ${fmt(c.median)}`,
            `Разброс: ${fmt(lo)} … ${fmt(hi)}`,
            `<span style="opacity:.6">снапшотов: ${c.samples} · ${isCoarsePointer() ? 'повторный тап' : 'клик'} — сверка дня</span>`,
          ]
            .filter(Boolean)
            .join('<br/>') + warn;
        },
      },
      xAxis: {
        type: 'category',
        data: candles.map((c) => fmtDate(c.date)),
        axisLabel: { color: CHART_COLORS.text, ...(narrow ? { fontSize: 10, hideOverlap: true } : {}) },
      },
      yAxis: {
        type: 'value',
        ...(axis ?? {}),
        axisLabel: valueAxisLabel(narrow),
        splitLine: { lineStyle: { color: CHART_COLORS.grid } },
      },
      series: [
        {
          name: 'Позиция за сутки',
          type: 'candlestick',
          data: candles.map((c) => c.candle),
          clip: true,
          itemStyle: {
            color: CHART_COLORS.neg,
            color0: CHART_COLORS.pos,
            borderColor: CHART_COLORS.neg,
            borderColor0: CHART_COLORS.pos,
          },
        },
        {
          name: 'Медиана дня',
          type: 'line',
          data: candles.map((c) => c.median ?? '-'),
          showSymbol: true,
          symbolSize: 5,
          lineStyle: { width: 1, type: 'dashed', color: '#7c3aed' },
          itemStyle: { color: '#7c3aed' },
        },
      ],
    };
  }, [candles, narrow]);

  if (isLoading) return <div className="h-40 animate-pulse rounded bg-bg3" />;
  if (candles.every((c) => c.samples === 0)) {
    return <div className="flex h-40 items-center justify-center text-sm text-text2">Нет снапшотов за период.</div>;
  }
  return (
    <>
      <EChart
        option={option}
        height={340}
        onGridClick={(idx) => {
          const c = candles[idx];
          if (c) onPickDay(c.date);
        }}
      />
      <p className="mt-1 text-xs text-text2">
        Свеча — открытие→закрытие суток (МСК) по устойчивым точкам: зелёная — позиция выросла, красная — снизилась.
        Усы — сырой разброс внутри дня (переводы «в пути»). Пунктир — медиана дня.
      </p>
    </>
  );
}
